interface CachedWorkspaceRead {
  promise: Promise<unknown>;
  value?: unknown;
  settledAt?: number;
  evictionTimer?: ReturnType<typeof setTimeout>;
}

interface WorkspaceReadInput<T> {
  workspaceId: string;
  key: string;
  load: () => Promise<T>;
  signal?: AbortSignal;
  ttlMs?: number;
}

const DEFAULT_TTL_MS = 5_000;
const cache = new Map<string, CachedWorkspaceRead>();

function cacheKey(workspaceId: string, key: string) {
  return `${workspaceId}:${key}`;
}

function raceWithSignal<T>(promise: Promise<T>, signal?: AbortSignal) {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(signal.reason);
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(signal.reason);
    signal.addEventListener('abort', abort, { once: true });
    promise.then(resolve, reject).finally(() => {
      signal.removeEventListener('abort', abort);
    });
  });
}

export function readWorkspaceCached<T>({
  workspaceId,
  key,
  load,
  signal,
  ttlMs = DEFAULT_TTL_MS,
}: WorkspaceReadInput<T>): Promise<T> {
  const id = cacheKey(workspaceId, key);
  const existing = cache.get(id);
  if (
    existing &&
    (existing.settledAt === undefined ||
      Date.now() - existing.settledAt <= ttlMs)
  ) {
    return raceWithSignal(
      existing.value === undefined
        ? (existing.promise as Promise<T>)
        : Promise.resolve(existing.value as T),
      signal
    );
  }

  const entry: CachedWorkspaceRead = {
    promise: Promise.resolve().then(load),
  };
  cache.set(id, entry);
  entry.promise = entry.promise.then(
    value => {
      entry.value = value;
      entry.settledAt = Date.now();
      entry.evictionTimer = setTimeout(
        () => {
          if (cache.get(id) === entry) cache.delete(id);
        },
        Math.max(0, ttlMs) + 1
      );
      return value;
    },
    error => {
      if (cache.get(id) === entry) cache.delete(id);
      throw error;
    }
  );
  return raceWithSignal(entry.promise as Promise<T>, signal);
}

export function invalidateWorkspaceRead(workspaceId: string, key?: string) {
  if (key) {
    const id = cacheKey(workspaceId, key);
    const entry = cache.get(id);
    if (entry?.evictionTimer) clearTimeout(entry.evictionTimer);
    cache.delete(id);
    return;
  }
  const prefix = `${workspaceId}:`;
  for (const [id, entry] of cache) {
    if (id.startsWith(prefix)) {
      if (entry.evictionTimer) clearTimeout(entry.evictionTimer);
      cache.delete(id);
    }
  }
}
